import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  clearAllFilters,
  clearFilter,
  filtersToSearchParams,
  parseFilters,
  type CatalogueFilters,
  type FilterChipKind,
} from '@/lib/filters';

interface UseCatalogueFilters {
  filters: CatalogueFilters;
  patchFilters: (patch: Partial<CatalogueFilters>) => void;
  removeFilter: (kind: FilterChipKind, value?: string) => void;
  resetFilters: () => void;
}

/**
 * The URL query string IS the filter state. Nothing is mirrored into component state, so a
 * bookmarked or shared URL always reproduces exactly the view the author was looking at.
 */
export function useCatalogueFilters(): UseCatalogueFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const patchFilters = useCallback(
    (patch: Partial<CatalogueFilters>) => {
      setSearchParams((current) => filtersToSearchParams({ ...parseFilters(current), ...patch }), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const removeFilter = useCallback(
    (kind: FilterChipKind, value?: string) => {
      setSearchParams(
        (current) => filtersToSearchParams(clearFilter(parseFilters(current), kind, value)),
        {
          replace: true,
        },
      );
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(() => {
    setSearchParams((current) => filtersToSearchParams(clearAllFilters(parseFilters(current))), {
      replace: true,
    });
  }, [setSearchParams]);

  return { filters, patchFilters, removeFilter, resetFilters };
}
